import '@components/Search.scss';
import { useEffect, useRef, useState } from 'react';
import { useColorScheme } from '@mui/joy/styles';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Avatar from '@mui/joy/Avatar';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import ListDivider from '@mui/joy/ListDivider';
import Drawer from '@mui/joy/Drawer';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import LinearProgress from '@mui/joy/LinearProgress';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import BookRoundedIcon from '@mui/icons-material/BookRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';

import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import WalletRoundedIcon from '@mui/icons-material/WalletRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';


import Navigation from './Navigation';
import ColorSchemeToggle from '@components/ColorSchemeToggle';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge, ModalOverflow } from '@mui/joy';
import { useAuth } from '@providers/AuthProvider';
import { useGetAllUserMeta } from '@api/userMetaService';
import MaestroLoader from '@components/MaestroLoader';
import Notifications from '@components/Notifications';
import { Command } from 'cmdk';
import { useHotkeys } from 'react-hotkeys-hook';
import { hasCmdKey } from '@utils/hasCmdKey';
import { useDebounce } from '@uidotdev/usehooks';
import { useGetAll } from '@api/crudService';
import { iconForObjectType } from '@utils/iconForObjectType';
import { detectObjectType } from '@utils/detectObjectType';
import pluralize from 'pluralize';
import { CheckPermission } from './CheckPermission';

interface KeywordWeight {
    [key: string]: number;
}

interface SearchResult {
    [key: string]: string;
}

function StackedNewIcon({children}) {
    return (
        <Box sx={{position: 'relative'}}>
            <Box sx={{
                position: 'absolute',
                display: 'inline-block',
                width: '1rem',
                height: '1rem',
                lineHeight: '1rem',
                bottom: '-0.35rem',
                right: '-0.5rem',
                'svg': {
                    width: '100%',
                    height: '100%',
                }
            }}>
                <AddRoundedIcon />
            </Box>
            <Box sx={{
                clipPath: 'polygon(0% 0%, 0% 100%, 60% 100%, 61% 88%, 62% 79%, 67% 70%, 75% 64%, 86% 61%, 100% 60%, 100% 0%)',
                width: '1.15rem',
                height: '1.15rem',
                'svg': {
                    width: '100%',
                    height: '100%',
                }
            }}>
                {children}
            </Box>
        </Box>
    );
}

const newItems = [
    {
        label: 'New Contact',
        link: '/contacts/create',
        icon: <StackedNewIcon><PeopleRoundedIcon /></StackedNewIcon>,
        color: 'contact',
        permission: 'edit contacts',
    },
    {
        label: 'New Work Plan',
        link: '/work-plans/create',
        icon: <StackedNewIcon><ChecklistRoundedIcon /></StackedNewIcon>,
        color: 'workplan',
        permission: 'edit work plans',
    },
    {
        label: 'New Business',
        link: '/businesses/create',
        icon: <StackedNewIcon><StoreRoundedIcon /></StackedNewIcon>,
        color: 'business',
        permission: 'edit businesses',
    },
    {
        label: 'New Property',
        link: '/properties/create',
        icon: <StackedNewIcon><ApartmentRoundedIcon /></StackedNewIcon>,
        color: 'property',
        permission: 'edit properties',
    },
    {
        label: 'New Report',
        link: '/reports/new',
        icon: <StackedNewIcon><QueryStatsRoundedIcon /></StackedNewIcon>,
        color: 'neutral',
        permission: 'edit reports'
    },
];

const items = [
    {
        label: 'Home / Dashboard',
        link: '/dashboard',
        icon: <HomeRoundedIcon />,
        color: 'neutral',
    },
    {
        label: 'Contacts',
        link: '/contacts',
        icon: <PeopleRoundedIcon />,
        color: 'contact',
        // shortcut: ['c'],
        permission: ['view basic contacts', 'view contacts'],
    },
    {
        label: 'Work Plans',
        link: '/work-plans',
        icon: <ChecklistRoundedIcon />,
        color: 'workplan',
    },
    {
        label: 'Businesses',
        link: '/businesses',
        icon: <StoreRoundedIcon />,
        color: 'business',
        permission: ['view basic businesses', 'view businesses'],
    },
    {
        label: 'Properties',
        link: '/properties',
        icon: <ApartmentRoundedIcon />,
        color: 'property',
        permission: ['view basic properties', 'view properties'],
    },
    {
        label: 'Events',
        link: '/events',
        icon: <CalendarMonthRoundedIcon />,
        color: 'event',
    },
    {
        label: 'Volunteer Opportunities',
        link: '/opportunities',
        icon: <Diversity3RoundedIcon />,
        color: 'neutral',
        permission: ['view basic work plans', 'view work plans'],
    },
    {
        label: 'Donations',
        link: '/donations',
        icon: <WalletRoundedIcon />,
        color: 'neutral',
        permission: ['view basic donations', 'view donations'],
    },
    {
        label: 'Reports',
        link: '/reports',
        icon: <QueryStatsRoundedIcon />,
        color: 'neutral',
        permission: 'edit reports',
    },
    {
        label: 'More...',
        link: '/settings',
        icon: <ConstructionRoundedIcon />,
        color: 'neutral',
        permission: ['view basic settings', 'view settings'],
    },
];

const FauxSearchBox = ({setIsOpen}) => (<>
    <Input
        size="sm"
        variant="outlined"
        placeholder="Search…"
        startDecorator={<SearchRoundedIcon color="primary" />}
        endDecorator={
            <IconButton
                variant="outlined"
                color="neutral"
                sx={{ bgcolor: 'background.level1', pointerEvents: 'none' }}
            >
                <Typography level="title-sm" textColor="text.icon">
                    {hasCmdKey() ? "⌘" : <Typography sx={{verticalAlign: 'middle'}} fontSize={10}>CTRL</Typography>}{` K`}
                </Typography>
            </IconButton>
        }
        sx={{
            maxWidth: '200px',
            alignSelf: 'center',
            display: {
                xs: 'none',
                md: 'flex',
            },
        }}
        onFocus={(e) => {
            setIsOpen(true);
            e?.target?.blur(); // this is required or browsers will stay focused permanently
        }}
    />
    <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        sx={{ display: { xs: 'inline-flex', md: 'none' }, alignSelf: 'center' }}
        onClick={() => setIsOpen(true)}
    >
        <SearchRoundedIcon />
    </IconButton>
</>);

const weightedKeywords:KeywordWeight = {
    name: 1,
    address: 1, // todo: figure out how to change this based on the result type (e.g. prop address should rank higher than contact address)

    company_name: 0.5,
    first_name: 0.5,
    last_name: 0.5,
    email: 0.5,
};

function getKeywordsForResult(result:SearchResult) {
    // get the possibilities we care about
    let possibilities = Object.keys(weightedKeywords);/* [
        'name',
        'first_name',
        'last_name',
        'email',
        'address',
    ];*/

    let keywords = [];
    for(const possibility of possibilities) {
        if(!!result[possibility]) {
            keywords.push(result[possibility]);
        }
    }

    // fallback to everything else
    // todo: establish what we actually care about and then remove this
    let ignoredColumns = [
        'created_at',
        'updated_at',
        'result_type',
    ];
    let finalKeyword:any[] = [];
    Object.entries(result).map(([resultKey, resultValue]) => {
        if(
            possibilities.indexOf(resultKey) === -1
            && ignoredColumns.indexOf(resultKey) === -1
            && !!resultValue
        ) {
            finalKeyword.push(resultValue);
        }
    });

    keywords.push(finalKeyword.join(" "));

    return keywords;
}

function filterSearchResults(value:string, search:string, keywords?: string[] | undefined):number {
    // search results are json, quick jump is string
    // let finalValue = value;
    try {
        value = JSON.parse(value);
    } catch(e) {
        // console.error(e);
        // is not json
    }

    let isSearchResult = (typeof value !== 'string');

    // filter should be case-insensitive
    search = search.toLocaleLowerCase();

    // special filtering for built in presets
    if(search[0] == '/') {
        if(isSearchResult) {
            return 0;
        }

        search = search.substring(1);
        return value.toLocaleLowerCase().indexOf(search) !== -1 ? 1 : 0;
    } /*else {
        if(!isSearchResult) {
            return false;
        }
    }*/

    // search for direct & starts with matches first
    // const keywords = getKeywordsForResult(value);
    for(const keyword of keywords) {
        if(keyword.toLocaleLowerCase().indexOf(search) !== -1) {
            return weightedKeywords?.[keyword] ?? 1;
        }
    }

    // look for indirect matches
    const splitSearchTerms = search.split(/\s+/);
    for (const splitSearch of splitSearchTerms) {
        for(const keyword of keywords) {
            if(keyword.toLocaleLowerCase().indexOf(splitSearch) !== -1) {
                return weightedKeywords?.[keyword] ?? 0.7;
            }
        }
    }

    return 0;
}

export default function Search() {
    const searchInputRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    // const [searchResults, setSearchResults] = useState([]);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data: searchResults,
        isFetching,
        isPreviousData,
    } = useGetAll({
        queryKey: ['search', debouncedSearchTerm],
        enabled: debouncedSearchTerm.length > 0 && debouncedSearchTerm[0] != '/',
    }, {
        term: encodeURIComponent(debouncedSearchTerm),
    });

    // const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    useHotkeys(
        'mod+k, meta+k',
        () => setIsOpen(true),
        {
            preventDefault: true,
        }
    );

    useEffect(() => {
        if(isOpen) {
            setTimeout(() => {
                try {
                    searchInputRef.current.focus();
                } catch(e) {

                }
            }, 50);
        } else {
            setSearchTerm(''); // reset search on close
        }
    }, [isOpen]);

    return (<>
        <FauxSearchBox setIsOpen={setIsOpen}/>


{/* <Command.Dialog open={isOpen} onOpenChange={setIsOpen}> */}


<Modal open={isOpen} onClose={() => {console.log('WILL CLOSE', isOpen);setIsOpen(false)}}>
    <ModalOverflow>
    <ModalDialog
        sx={{
            p: 0,
            boxShadow: '0',
            width: '100%',
            maxWidth: '40rem',
            height: {
                xs: '100vh',
                md: 'unset',
            }
        }}
    >
        {/* <Typography>Modal title</Typography> */}
        <Command
            loop
            filter={filterSearchResults}
        >
        <ModalClose />
            {/* Type a command or search... */}
            <Command.Input
                autoFocus
                ref={searchInputRef}
                placeholder="Type to search or press / for commands"
                onValueChange={setSearchTerm}
                value={searchTerm}
            />

            <Command.List>
                {/* {isLoading && <Command.Loading>Searching…</Command.Loading>} */}
                {isLoading && <Box sx={{px: '0.5rem'}}><LinearProgress/></Box>}
                {isLoading === false && debouncedSearchTerm.length > 0 && <Command.Empty>No results found.</Command.Empty>}
                {isLoading === false && debouncedSearchTerm.length === 0 && <Command.Empty>Type to search or press / for commands</Command.Empty>}
                {/* TODO: display +new buttons on the no results found screen */}

            {searchTerm.length > 0 && searchResults?.data && <>
                <Command.Group heading="Search Results">
                    {searchResults.data.map((item) => {
                        const objectType = detectObjectType(item);
                        const ResultIcon = iconForObjectType(objectType, false);

                        let nameForItem = 'N/A';
                        if(item?.name) {
                            nameForItem = item.name;
                        } else if(item?.first_name) {
                            nameForItem = `${item.first_name} ${item?.last_name ?? ''}`.trim();
                        } else if(item?.company_name) {
                            nameForItem = item.company_name;
                        } else if(item?.address) {
                            nameForItem = item.address;
                        }

                        return (
                        <Command.Item
                            key={`${item.id}`}
                            value={JSON.stringify(item)}
                            keywords={getKeywordsForResult(item)}
                            data-color={objectType ?? 'neutral'}
                            onSelect={() => {
                                if(objectType) {
                                    let endpoint = pluralize(objectType);
                                    if(objectType === 'workplan') {
                                        endpoint = 'work-plans';
                                    }
                                    navigate(`/${endpoint}/${item.id}`); // todo: don't rely on pluralization
                                    setIsOpen(false);
                                }
                            }}
                        >
                            {objectType && <ResultIcon/>}
                            {nameForItem}
                        </Command.Item>
                        )
                    })}
                </Command.Group>
            </>}

            {searchTerm[0] == '/' && (
            <Command.Group heading="Quick Jump">
                {items.map(({ icon, label, color = '', link = null, shortcut = null, permission = null }) => {
                    return (
                    <CheckPermission permissions={permission === null || permission}>
                    <Command.Item key={label} value={label} data-color={color} onSelect={() => {
                        if(link) {
                            navigate(link);
                            setIsOpen(false);
                        }
                    }}>
                        {icon}
                        {label}
                        {/*
                        {shortcut && (
                            <div cmdk-shortcuts="">
                            { && shortcut.map((key) => {
                                return <kbd key={key}>{key}</kbd>
                            })}
                            </div>
                        )}
                        */}
                    </Command.Item>
                    </CheckPermission>
                    )
                })}
            </Command.Group>
            )}

            {searchTerm[0] == '/' && (
            <Command.Group heading="Create New">
            {newItems.map(({ icon, label, color = '', link = null, permission = null }) => {
                return (
                    <CheckPermission permissions={permission === null || permission}>
                    <Command.Item key={label} value={label} data-color={color} onSelect={() => {
                        if(link) {
                            navigate(link);
                            setIsOpen(false);
                        }
                    }}>
                        {icon}
                        {label}
                        {/*
                        {shortcut && (
                            <div cmdk-shortcuts="">
                            { && shortcut.map((key) => {
                                return <kbd key={key}>{key}</kbd>
                            })}
                            </div>
                        )}
                        */}
                    </Command.Item>
                    </CheckPermission>
                    )
            })}
            </Command.Group>
            )}
        </Command.List>
        </Command>
    </ModalDialog>
    </ModalOverflow>
</Modal>


{/* </Command.Dialog> */}
    </>);
}

